"use client";

import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import { Link } from "@commerce-frontend/ui/base/Link/Link";
import { useWindowWidth } from "@commerce-frontend/ui/hooks/responsive";
import { cn } from "@commerce-frontend/ui/utils/classNames";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import type { FragmentOf } from "gql.tada";
import { usePathname } from "next/navigation";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import type { MenuItemFragment } from "../Header.fragments";
import { SubNavigation } from "./DesktopSubNavigation";

type Props = {
	topLevelMenuItems: FragmentOf<typeof MenuItemFragment>[];
};

export const DesktopMainMenu = ({ topLevelMenuItems }: Props) => {
	const { windowWidth } = useWindowWidth();

	const ref = useRef<HTMLDivElement>(null);
	useCloseMenuOnNavigation(ref);

	return windowWidth.atLeastDesktop ? (
		// we use first-child because NavigationMenu.List wraps in a div that cannot be classed
		<NavigationMenu.Root className="relative col-fullbleed hidden grid-cols-constrain bg-main-50 lg:grid [&>*:first-child]:col-main">
			<NavigationMenu.List className="col-main flex p-0">
				{topLevelMenuItems.map((mainMenuItem, i) => (
					<NavigationMenu.Item
						key={mainMenuItem.label}
						{...storyblokDataAttributes(mainMenuItem)}
						className={cn(
							i === 0 && "-ml-3",
							i === topLevelMenuItems.length - 1 && "-mr-3",
						)}
					>
						{mainMenuItem.subMenuItems ? (
							<>
								<NavigationMenu.Trigger
									className={cn(
										"group inline-flex py-4 text-sm font-bold leading-6 text-main xl:text-base",
										"px-3",
									)}
									asChild
								>
									<Link href={mainMenuItem.link.url} className="w-full">
										{mainMenuItem.label}
									</Link>
								</NavigationMenu.Trigger>

								<NavigationMenu.Content
									ref={ref}
									className={cn(
										"absolute left-0 top-0 grid max-h-[calc(100lvh-10.5rem)] w-full grid-cols-constrain overflow-y-auto overscroll-contain bg-background py-6 text-main",
										"data-[motion=from-end]:animate-fadeIn data-[motion=from-start]:animate-fadeIn data-[motion=to-end]:animate-fadeOut data-[motion=to-start]:animate-fadeOut",
									)}
								>
									<SubNavigation menuItem={mainMenuItem} />
								</NavigationMenu.Content>
							</>
						) : (
							<>
								<Link
									className="inline-flex rounded-none py-4 text-sm font-bold leading-6 text-main xl:text-base"
									href={mainMenuItem.link.url}
								>
									{mainMenuItem.label}
								</Link>
							</>
						)}
					</NavigationMenu.Item>
				))}

				<NavigationMenu.Indicator className="top-[calc(100%-3px)] z-10 flex h-[3px] origin-center px-3 transition-all">
					<div className="w-full bg-main" />
				</NavigationMenu.Indicator>
			</NavigationMenu.List>
			<div className="absolute left-0 top-full col-fullbleed flex w-full">
				<NavigationMenu.Viewport className="relative h-[var(--radix-navigation-menu-viewport-height)] w-full bg-background shadow-object transition-all after:pointer-events-none after:fixed after:inset-0 after:top-52 after:-z-10 after:bg-black after:opacity-0 data-[state=open]:after:opacity-25" />
			</div>
		</NavigationMenu.Root>
	) : undefined;
};

// We don't use the existing NavigationMenuLink because that closes the menu on click.
// Instead, we want to close the menu on navigation.
const useCloseMenuOnNavigation = (ref: RefObject<HTMLDivElement | null>) => {
	const pathname = usePathname();

	useEffect(() => {
		if (ref.current) {
			ref.current.dispatchEvent(
				new CustomEvent("navigationMenu.rootContentDismiss", {
					bubbles: true,
					cancelable: true,
				}),
			);
		}
	}, [pathname, ref]);
};
